import * as Yup from 'yup';

import { createEmailValidation } from '@/utils/emailValidation';

const MAX_PHONE_LENGTH_BY_COUNTRY = {
  MX: 13, // México
  PE: 12, // Perú
  CO: 13, // Colombia
  CL: 12, // Chile
};

const MAX_PHONE_LENGTH_BY_COUNTRY_WITHOUT_CODE = {
  MX: 10,
  PE: 9,
  CO: 10,
  CL: 9,
};

export const passwordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Este campo es requerido'),
  newPassword: Yup.string().required('Este campo es requerido'),
  confirmNewPassword: Yup.string().oneOf(
    [Yup.ref('newPassword'), undefined],
    'Las contraseñas no coinciden',
  ),
});

const profileSchema = Yup.object().shape({
  address: Yup.string(), // No required - se mantiene del perfil actual
  firstName: Yup.string()
    .required('Este campo es requerido')
    .max(50, 'El campo no puede tener más de 50 caracteres')
    .matches(
      /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
      'Solo se permiten letras, espacios y caracteres especiales comunes (acentos)',
    )
    .test(
      'not-only-spaces',
      'El campo no puede contener solo espacios',
      (value) => value?.trim().length > 0,
    ),
  lastName: Yup.string()
    .required('Este campo es requerido')
    .max(50, 'El campo no puede tener más de 50 caracteres')
    .matches(
      /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
      'Solo se permiten letras, espacios y caracteres especiales comunes (acentos)',
    )
    .test(
      'not-only-spaces',
      'El campo no puede contener solo espacios',
      (value) => value?.trim().length > 0,
    ),
  phone: Yup.string()
    .required('Este campo es requerido')
    .matches(/^[\d+]+$/, 'El número de teléfono solo puede contener caracteres numéricos')
    .when('$country', (country, schema) => {
      if (!country[0]) {
        return schema.test(
          'is-country-selected',
          'El código de país es incorrecto', // Mensaje de error
          () => false, // Siempre falla la validación
        );
      }
      const maxLength =
        MAX_PHONE_LENGTH_BY_COUNTRY[
          country[0] as unknown as keyof typeof MAX_PHONE_LENGTH_BY_COUNTRY
        ] || 12;

      const maxLengthWithoutCode =
        MAX_PHONE_LENGTH_BY_COUNTRY_WITHOUT_CODE[
          country[0] as unknown as keyof typeof MAX_PHONE_LENGTH_BY_COUNTRY_WITHOUT_CODE
        ] || 10;

      return schema
        .max(maxLength, `El número debe tener máximo ${maxLengthWithoutCode} dígitos`)
        .min(maxLength, `El número debe tener mínimo ${maxLengthWithoutCode} dígitos`);
    }),
});

const MexCompanySchema = Yup.object().shape({
  address: Yup.string().required('Este campo es requerido'),
  cfdiUse: Yup.string().required('Este campo es requerido'),
  cfdiDescription: Yup.string(), // Descripción del CFDI seleccionado
  companyType: Yup.string().oneOf(['naturalPerson', 'corporateEntity']),
  email: createEmailValidation('Este campo requiere un email válido con dominio'),
  name: Yup.string().required('Este campo es requerido'),
  legalName: Yup.string().required('Este campo es requerido'),
  phone: Yup.string().required('Este campo es requerido'),
  packageSize: Yup.string().default('1'),
  proofTaxSituationFile: Yup.string(),
  requireInvoice: Yup.boolean(),
  rfc: Yup.string()
    .required('Este campo es requerido')
    .matches(/^[A-Z0-9]*$/, 'Solo se permiten números y letras mayúsculas')
    .when('companyType', {
      is: (companyType: string) => companyType === 'corporateEntity',
      then: (schema) =>
        schema.test('len', 'Este campo debe tener 12 caracteres', (val) => val?.length === 12),
      otherwise: (schema) =>
        schema.test('len', 'Este campo debe tener 13 caracteres', (val) => val?.length === 13),
    }),
  taxRegime: Yup.string().required('Este campo es requerido'),
  taxRegimeDescription: Yup.string(), // Descripción del régimen fiscal seleccionado
  zipCode: Yup.string()
    .required('Este campo es requerido')
    .matches(/^\d+$/, 'El código postal solo debe contener números')
    .length(5, 'El código postal debe tener exactamente 5 dígitos'),
});

const getOtherCompanySchema = (country: string) => {
  return Yup.object().shape({
    name: Yup.string().required('Este campo es requerido'),
    companyName: Yup.string().required('Este campo es requerido'),
    companyType: Yup.string().oneOf(['naturalPerson', 'corporateEntity']),
    identification: Yup.string().required('Este campo es requerido'),
    address: Yup.string().required('Este campo es requerido'),
    email: createEmailValidation('Este campo requiere un email válido con dominio'),
    packageSize: Yup.string().default('1'),
    phone: Yup.string(),
    // Zipcode: NO requerido para Perú, requerido para Colombia y Chile
    zipCode:
      country !== 'PER'
        ? Yup.string()
            .matches(/^[0-9]*$/, 'Solo se permiten números')
            .required('Este campo es requerido')
        : Yup.string(),
    // regimeDescription: REQUERIDO solo para Colombia
    regimeDescription:
      country === 'COL' ? Yup.string().required('Este campo es requerido') : Yup.string(),
    // invoiceUsageDescription: REQUERIDO solo para Chile
    invoiceUsageDescription:
      country === 'CHL' ? Yup.string().required('Este campo es requerido') : Yup.string(),
  });
};

export const getSchemaByCountry = (country: string) => {
  const baseSchema = {
    profile: profileSchema,
    company: Yup.mixed(),
  };
  if (country === 'MEX') {
    return Yup.object().shape({
      ...baseSchema,
      company: MexCompanySchema,
    });
  } else {
    return Yup.object().shape({
      ...baseSchema,
      company: getOtherCompanySchema(country),
    });
  }
};

export type SchemaType = Yup.InferType<ReturnType<typeof getSchemaByCountry>>;
